<template>
  <div class="modern-login-root">
    <!-- 粒子背景层 -->
    <div ref="particleLayer" class="particle-bg"></div>
    <div class="modern-login-split">
      <!-- 左侧展示区 -->
      <div class="modern-login-left">
        <div class="header-title">
          <span class="glow-text">云商城 - 微服务交易平台</span>
        </div>
        <div class="left-content">
          <Cart3D style="margin-bottom: 32px;" />
          <div class="typewriter">
            <span>{{ slogan }}</span>
            <span class="type-cursor">|</span>
          </div>
        </div>
      </div>
      <!-- 右侧表单区 -->
      <div class="modern-login-right">
        <div class="glassmorphism login-card">
          <h2 class="glow-text" style="margin-bottom: 18px;">欢迎登录</h2>
          <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" class="login-form" @keyup.enter="handleLogin">
            <el-form-item prop="username">
              <el-input v-model="loginForm.username" placeholder="用户名" clearable autofocus />
            </el-form-item>
            <el-form-item prop="password">
              <el-input v-model="loginForm.password" type="password" placeholder="密码" show-password />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" class="ripple login-btn" :loading="loading" @click="handleLogin" style="width:100%;font-size:18px;">登 录</el-button>
            </el-form-item>
            <el-form-item style="margin-bottom: 8px;">
              <div class="demo-login-wrap">
                <el-tooltip content="演示账号一键登录" placement="top">
                  <el-button circle class="demo-icon-btn" @click="loginWithDemo" :loading="loading">
                    <el-icon><UserFilled /></el-icon>
                  </el-button>
                </el-tooltip>
              </div>
            </el-form-item>
            <el-form-item style="margin-bottom:0;">
              <el-link type="info" @click="goToRegister">没有账号？立即注册</el-link>
            </el-form-item>
            <el-form-item style="margin-bottom:0; margin-top: 8px;">
              <div class="portal-entry-row">
                <el-link type="primary" @click="goToMerchantLogin">商家端登录</el-link>
                <el-link type="warning" @click="goToAdminLogin">管理端登录</el-link>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
    <!-- 加载动画 -->
    <el-dialog 
      v-model="showLoading" 
      width="300px" 
      :show-close="false" 
      :close-on-click-modal="false" 
      :close-on-press-escape="false" 
      align-center
      class="loading-dialog"
    >
      <div class="loading-content">
        <div class="loading-spinner">
          <el-icon :size="64" color="#a3a3ff" class="spin-icon"><Loading /></el-icon>
        </div>
        <div class="loading-text">登录中...</div>
        <div class="loading-subtext">正在跳转到云商城</div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElNotification, ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { authAPI } from '../api/auth'
import Cart3D from './Cart3D.vue'
import { useTypewriter } from './useTypewriter'

const router = useRouter()
const loginForm = reactive({ username: '', password: '' })
const loginFormRef = ref(null)
const loading = ref(false)
const showLoading = ref(false)
const loginRules = {
  username: [ { required: true, message: '请输入用户名', trigger: 'blur' } ],
  password: [ { required: true, message: '请输入密码', trigger: 'blur' } ]
}

// 打字机标语
const slogan = useTypewriter([
  '探索未来交易的无限可能',
  '安全 · 高效 · 智能 · 云端',
  '让每一次交易更简单',
])

const handleLogin = async () => {
  if (!loginFormRef.value) return
  try {
    const valid = await loginFormRef.value.validate()
    if (!valid) return
    loading.value = true
    const response = await authAPI.login(loginForm.username, loginForm.password)
    const { token, user, success, message, data } = response.data || {}
    // 兼容多种返回结构：直接作用或嵌套在data字段内
    const finalToken = token || data?.token
    const finalUser = user || data?.user
    const isSuccess = success !== false && (finalToken && finalUser)
    
    if (isSuccess) {
      localStorage.setItem('portalRole', 'USER')
      localStorage.setItem('token', finalToken)
      localStorage.setItem('user', JSON.stringify(finalUser))
      showLoading.value = true
      setTimeout(() => {
        showLoading.value = false
        ElNotification({
          title: '欢迎回来',
          message: `Hi，${finalUser.nickname || finalUser.username || loginForm.username}，欢迎登录云商城！`,
          type: 'success',
          duration: 2200,
        })
        router.push('/home')
      }, 1200)
    } else {
      ElMessage.error(message || '用户名或密码错误')
    }
  } catch (error) {
    console.error('登录异常:', error)
    const errMsg = error.response?.data?.message || error.message || '网络或服务器连接失败，请检查后端服务是否运行'
    ElMessage.error(errMsg)
  } finally {
    loading.value = false
  }
}

const loginWithDemo = async () => {
  loginForm.username = 'admin'
  loginForm.password = '123456'
  await handleLogin()
}

const goToRegister = () => {
  router.push('/register')
}

const goToMerchantLogin = () => {
  router.push('/merchant/login')
}

const goToAdminLogin = () => {
  router.push('/admin/login')
}

// 动态生成粒子
const particleLayer = ref(null)
onMounted(() => {
  const layer = particleLayer.value
  if (!layer) return
  // 清空
  layer.innerHTML = ''
  for (let i = 0; i < 32; i++) {
    const p = document.createElement('div')
    p.className = 'particle'
    const size = Math.random() * 18 + 12
    p.style.width = `${size}px`
    p.style.height = `${size}px`
    p.style.left = `${Math.random() * 100}%`
    p.style.top = `${Math.random() * 100}%`
    p.style.animationDuration = `${10 + Math.random() * 8}s`
    p.style.opacity = `${0.18 + Math.random() * 0.25}`
    layer.appendChild(p)
  }
})
</script>

<style scoped>
.modern-login-root {
  width: 100vw;
  height: 100vh;
  min-height: 600px;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.modern-login-split {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  position: relative;
  z-index: 2;
}
.modern-login-left {
  width: 60vw;
  min-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 7vh;
  background: transparent;
  user-select: none;
}
.header-title {
  font-size: 2.2rem;
  margin-bottom: 36px;
  letter-spacing: 2px;
  text-align: center;
}
.left-content {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.typewriter {
  font-size: 1.4rem;
  color: #fff;
  margin-top: 12px;
  min-height: 2.2em;
  letter-spacing: 1.5px;
  font-family: 'JetBrains Mono', 'Consolas', monospace;
  background: rgba(76, 32, 103, 0.08);
  border-radius: 8px;
  padding: 8px 18px;
  box-shadow: 0 2px 12px 0 #4b206720;
  display: inline-block;
}
.type-cursor {
  color: #a3a3ff;
  animation: blink 1.1s steps(1) infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.modern-login-right {
  width: 40vw;
  min-width: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 3;
}
.login-card {
  width: 370px;
  padding: 38px 36px 28px 36px;
  box-sizing: border-box;
  margin: 0 auto;
  box-shadow: 0 8px 32px 0 #4b206733, 0 1.5px 8px 0 #232a4d22;
}
.login-form {
  margin-top: 8px;
}
.login-btn {
  margin-top: 8px;
  font-weight: bold;
  letter-spacing: 1px;
  border-radius: 8px;
  height: 44px;
}

.portal-entry-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.demo-login-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
}

.demo-icon-btn {
  background: rgba(163, 163, 255, 0.16);
  border-color: rgba(163, 163, 255, 0.35);
  color: #dcdcff;
}

/* 加载动画样式 */
.loading-dialog :deep(.el-dialog) {
  background: rgba(35, 42, 77, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(163, 163, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.loading-dialog :deep(.el-dialog__body) {
  padding: 40px 30px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  position: relative;
}

.spin-icon {
  animation: spin 1s linear infinite;
  filter: drop-shadow(0 0 12px rgba(163, 163, 255, 0.6));
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 1px;
}

.loading-subtext {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@media (max-width: 900px) {
  .modern-login-left { display: none; }
  .modern-login-right { width: 100vw; min-width: unset; }
}
</style>